<template>
    <section class="header-user-slice">
        <div v-if="!loggedIn" class="unlogged-section">
            <template v-if="$ff('enable-auth-link')">
                <AuthLink
                    class="auth-button sign-up-button"
                    data-testid="navbar-signup-cta"
                    role="signup"
                    :redirect="redirectUrl"
                    :page-source="pageSource"
                    component-source="navbar-unlogged"
                >
                    {{ slice.primary.signUpText }}
                </AuthLink>
                <AuthLink
                    class="auth-button sign-in-button"
                    data-testid="navbar-signin-cta"
                    role="signin"
                    :redirect="redirectUrl"
                    :page-source="pageSource"
                    component-source="navbar-unlogged"
                >
                    {{ slice.primary.signInText }}
                </AuthLink>
            </template>
            <template v-else>
                <a class="sign-up-button" :href="signupUrl" data-testid="navbar-signup-cta" rel="nofollow">
                    {{ slice.primary.signUpText }}
                </a>
                <a class="sign-in-button" :href="signinUrl" data-testid="navbar-signin-cta" rel="nofollow">
                    {{ slice.primary.signInText }}
                </a>
            </template>
        </div>
        <div v-else class="logged-section">
            <div v-for="(link, i) in slice.items" :key="`slice-link-${i}`" class="link">
                <ServerSideNavigationPrismicLink :field="link.loggedInLink" class="header-link">
                    {{ link.loggedInTextLink }}
                </ServerSideNavigationPrismicLink>
            </div>
            <ServerSideNavigationPrismicLink :field="slice.primary.userDashboardLink" class="dashboard-link">
                <div class="user">
                    <div class="user-image">
                        <MaltImg v-if="image" :src="image" alt="user-photo" loading="lazy" height="30" width="30" fit="cover" />
                        <VJoyAvatar v-else size="small" :full-name="fullname" />
                    </div>
                    <div class="user-identity-text">
                        <span class="user-names" data-testid="header-user-names">
                            {{ fullname }}
                        </span>
                        <span v-if="!isCompany">
                            {{ slice.primary.userDashboardText }}
                        </span>
                    </div>
                </div>
            </ServerSideNavigationPrismicLink>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import ServerSideNavigationPrismicLink from '@navbar-unlogged/components/ServerSideNavigationPrismicLink.vue';
    import {useNavbarUser} from '@navbar-unlogged/composables';
    import {usePageSource} from '@navbar-unlogged/composables/usePageSource';
    import {getSliceComponentProps} from '@prismicio/vue';
    import type {HeaderUserSliceSlice} from '@prismicio-types';
    import {useRequestURL} from '#app';
    import {AuthLink} from '#components';
    import {MaltImg} from '#components';
    import {useLocalePrefix} from '#imports';
    import {VJoyAvatar} from '@maltjoy/core-vue';

    defineProps(getSliceComponentProps<HeaderUserSliceSlice>());

    // Composables
    const {user, loggedIn, isCompany} = useNavbarUser();
    const {pageSource} = usePageSource();
    const url = useRequestURL();
    const {withLocalePrefix} = useLocalePrefix();

    // Computed values
    const redirectUrl = computed(() => url.pathname + url.search);
    const maybeRedirectParam = computed(() => `&redirect=${encodeURIComponent(url.pathname + url.search)}`);
    const signinUrl = computed(
        () => `${withLocalePrefix('/signin')}?pageSource=${pageSource.value}&componentSource=navbar-unlogged${maybeRedirectParam.value}`,
    );
    const signupUrl = computed(
        () => `${withLocalePrefix('/who-are-you')}?pageSource=${pageSource.value}&componentSource=navbar-unlogged${maybeRedirectParam.value}`,
    );

    const fullname = computed(() => user.value?.username);

    const image = computed(() => {
        const url = user.value?.photo?.absoluteUrl;
        return !url ? '' : url;
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@navbar-unlogged/scss/variables.scss' as vars;

    .header-user-slice {
        font-family: var(--joy-font-family-base);
        @include vars.flex-row-center;

        a {
            text-decoration: none;
        }

        .sign-up-button {
            text-decoration: none;
            @include vars.button-primary-red;
            @include vars.button-small;

            @include mq.screen_md {
                width: 100%;
            }

            .theme-strategy & {
                @include vars.button-primary-blue;
                @include vars.button-small;
            }
        }

        .sign-in-button {
            text-decoration: none;
            @include vars.link;
            margin-left: 16px;
            font-size: var(--joy-font-size-primary-300);
            line-height: var(--joy-line-height-large);

            @include mq.screen_md {
                margin: 0;
                transform: translateX(calc(13px - 25vw));
            }
        }

        .auth-button {
            cursor: pointer;
            user-select: none;
            white-space: nowrap;
            &.sign-up-button {
                position: relative;
                top: -1px;
            }
        }

        @include mq.screen_md {
            width: 100%;
            margin: 0;
        }

        .header-link {
            color: var(--joy-color-neutral-50);
            font-size: var(--joy-font-size-primary-300);
        }

        .unlogged-section {
            @include vars.flex-row-center;

            @include mq.screen_md {
                width: 100%;
                flex-direction: column;
                gap: 30px;
            }
        }

        .logged-section {
            gap: 24px;
            @include vars.flex-row-center;

            @include mq.screen_md {
                margin-top: 32px;
                flex-direction: column;
                align-items: flex-start;
                width: 100%;
                padding-bottom: 32px;
                border-bottom: solid 1px var(--joy-color-neutral-30);

                a {
                    width: 100%;
                    font-size: var(--joy-font-size-primary-400);
                    margin-left: 0;
                }

                .dashboard-link {
                    order: -1;
                    padding-bottom: 32px;
                    border-bottom: solid 1px var(--joy-color-neutral-30);
                    margin-bottom: 8px;
                    font-size: var(--joy-font-size-primary-600);
                }
            }
        }

        .user {
            display: flex;
            text-align: left;
            align-items: center;
            background-color: transparent;
            color: var(--joy-color-neutral-60);
            border: none;
            line-height: vars.$line-height-xl;
            color: var(--joy-color-neutral-60);
            position: relative;
            font-size: var(--joy-font-size-primary-200);
            padding: 0;

            .user-identity-text {
                display: flex;
                flex-direction: column;
            }

            &:hover,
            &:focus,
            &:active,
            &:focus {
                color: var(--joy-color-primary-70);
                border: none;
                box-shadow: none;
            }

            &:after {
                background: none repeat scroll 0 0 transparent;
                background: var(--joy-color-primary-30);
                content: '';
                display: block;
                left: 50%;
                position: absolute;
                bottom: -13px;
                width: 0;
                transition:
                    width var(--joy-transition-duration-long) var(--joy-transition-timing-function),
                    var(--joy-transition-duration-long) var(--joy-transition-timing-function);
                width: 0;
                height: 4px;
                border-radius: var(--joy-core-radius-2);
            }

            &:hover:after {
                width: 100%;
                left: 0;
            }

            img {
                min-width: 100%;
                height: 100%;
                border-radius: var(--joy-core-radius-6);
                object-fit: cover;
            }

            .user-image {
                width: 30px;
                height: 30px;
                margin-right: 8px;
            }

            .user-names {
                font-weight: var(--joy-font-weight-bold);
            }
        }

        .link {
            padding: 0 12px;

            @include mq.screen_md {
                padding: 0;
                width: 100%;
                display: flex;
            }
        }
    }
</style>
